import React, { useEffect, useState } from "react";
import { motion,  AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
} from "@mui/material";
import {
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  ArrowUpward,
  Phone,
  Email,
  LocationOn,
  ChevronRight,
  Business,
  Security,
  SupportAgent,
  Code,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

/*
  Galaxy Motion Footer
  - Premium Galaxy / Milky-way background
  - Morphing glass panels, subtle parallax stars
  - Framer Motion for smooth anims
  - Fully responsive and mobile friendly

  Usage:
  1. npm i framer-motion @mui/material @emotion/react @emotion/styled @mui/icons-material
  2. import FooterGalaxy from './FooterGalaxy'
  3. <FooterGalaxy /> at bottom of page
*/

const socialLinks = [
  { icon: LinkedIn, url: "#", label: "LinkedIn" },
  { icon: Twitter, url: "#", label: "Twitter" },
  { icon: Instagram, url: "#", label: "Instagram" },
  { icon: Facebook, url: "#", label: "Facebook" },
];

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Transition", path: "/transition" },
  { name: "Reports", path: "/reports" },
  { name: "Contact", path: "/contact" },
];

const services = [
  "Housekeeping Services",
  "Landscaping & Space Utilization",
  "Pest Management",
  "Mechanized Laundry",
  "Catering & Guesthouse",
  "Civil & MEP Services",
];

const developerInfo = {
  company: "Aediax Tech Private Limited",
  phone: "+917854998757",
  email: "developer@aediax.com",
};

export default function FooterGalaxy() {
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 240);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const starVariants = {
    float: (i) => ({
      y: [0, -18 - (i % 5) * 6, 0],
      x: [0, (i % 2 === 0 ? -6 : 6), 0],
      transition: {
        duration: 8 + (i % 6),
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
        delay: i * 0.4,
      },
    }),
  };

  const nebulaVariants = {
    animate: {
      backgroundPosition: ["0% 50%", "50% 100%", "100% 50%"],
      transition: { duration: 20, repeat: Infinity, ease: "linear" },
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 12 },
        background: "radial-gradient(ellipse at 20% 10%, rgba(10,12,30,0.9), transparent 20%), linear-gradient(180deg, #06060a 0%, #071428 40%, #03061a 100%)",
      }}
    >
      {/* --- Galaxy/nebula background layer --- */}
      <motion.div
        aria-hidden
        variants={nebulaVariants}
        animate="animate"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(135deg, rgba(60,10,120,0.12), rgba(10,80,160,0.08)), radial-gradient(circle at 60% 40%, rgba(120,40,200,0.12), transparent 10%)",
          backgroundBlendMode: "screen",
          filter: "blur(30px) saturate(120%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* --- Subtle noise & vignette --- */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.02) 0%, transparent 40%), linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.6))",
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      {/* --- Parallax stars (motion) --- */}
      {[...Array(18)].map((_, i) => {
        const left = `${(i * 7) % 100}%`;
        const top = `${(i * 11) % 100}%`;
        const size = 1 + (i % 4) * 1.2;
        return (
          <motion.div
            key={i}
            custom={i}
            variants={starVariants}
            animate="float"
            style={{
              position: "absolute",
              top,
              left,
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.95), rgba(255,255,255,0.35))",
              boxShadow: "0 0 8px rgba(130,180,255,0.18)",
              zIndex: 2,
              opacity: 0.85,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* --- Large morphing blob (SVG) --- */}
      <svg
        viewBox="0 0 800 400"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          right: "-15%",
          top: "-8%",
          width: "70%",
          height: "60%",
          zIndex: 1,
          opacity: 0.16,
          pointerEvents: "none",
        }}
      >
        <defs>
          <linearGradient id="g1" x1="0%" x2="100%">
            <stop offset="0%" stopColor="#5b21b6" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#0ea5e9" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        <motion.path
          d="M0 200 C 100 100, 200 0, 300 50 C 420 120, 520 300, 700 240 C 760 220, 840 200, 800 400 L 0 400 Z"
          fill="url(#g1)"
          initial={{ d: "M0 200 C 100 100, 200 0, 300 50 C 420 120, 520 300, 700 240 C 760 220, 840 200, 800 400 L 0 400 Z" }}
          animate={{
            d: [
              "M0 200 C 100 120, 220 0, 300 70 C 420 140, 500 320, 700 260 C 760 240, 840 200, 800 400 L 0 400 Z",
              "M0 220 C 80 80, 200 20, 320 50 C 450 120, 560 300, 720 230 C 760 200, 840 180, 800 400 L 0 400 Z",
              "M0 200 C 100 100, 200 0, 300 50 C 420 120, 520 300, 700 240 C 760 220, 840 200, 800 400 L 0 400 Z",
            ],
            transition: { duration: 18, repeat: Infinity, ease: "easeInOut" },
          }}
        />
      </svg>

      <Container sx={{ position: "relative", zIndex: 3 }}>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                variant="h5"
                fontWeight={800}
                letterSpacing={1}
                sx={{
                  textTransform: "uppercase",
                  background: "linear-gradient(90deg,#a78bfa,#60a5fa,#1e3a8a)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                }}
              >
                Namami India
              </Typography>

              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mb: 2 }}>
                Leading Integrated Facility Management solutions — efficient, safe,
                and innovation-driven across India.
              </Typography>

              <Stack spacing={1} alignItems={{ xs: "center", md: "flex-start" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOn sx={{ color: "#7dd3fc" }} />
                  <Typography variant="body2" color="rgba(255,255,255,0.8)">
                    Behind Gourishankar English Medium School, Dhauli, Bhubaneswar, Odisha – 751002
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Phone sx={{ color: "#7dd3fc" }} />
                  <MuiLink href="tel:+919692783839" color="inherit" underline="hover">
                    +91 96927 83839
                  </MuiLink>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Email sx={{ color: "#7dd3fc" }} />
                  <MuiLink href="mailto:info@namamiindia.co.in" color="inherit" underline="hover">
                    info@namamiindia.co.in
                  </MuiLink>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#9be7ff" }}>
              Quick Links
            </Typography>
            <List dense sx={{ p: 0 }}>
              {quickLinks.map((l) => (
                <ListItem key={l.name} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28, color: "#60a5fa" }}>
                    <ChevronRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<MuiLink component={Link} to={l.path} sx={{ color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>{l.name}</MuiLink>}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#9be7ff" }}>
              Our Services
            </Typography>
            <List dense sx={{ p: 0 }}>
              {services.map((s) => (
                <ListItem key={s} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28, color: "#60a5fa" }}>
                    <ChevronRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={<Typography variant="body2" sx={{ fontSize: 13 }}>{s}</Typography>} />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: { xs: "center", md: "right" } }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#9be7ff" }}>
                Connect With Us
              </Typography>

              <Stack direction="row" justifyContent={{ xs: "center", md: "flex-end" }} spacing={1} mb={2}>
                {socialLinks.map((s, i) => (
                  <IconButton
                    key={i}
                    component="a"
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 44,
                      height: 44,
                      background: "rgba(255,255,255,0.04)",
                      borderRadius: 2,
                      backdropFilter: "blur(6px)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      '&:hover': { transform: 'translateY(-4px) scale(1.04)', boxShadow: '0 10px 30px rgba(59,130,246,0.14)' },
                    }}
                  >
                    <s.icon fontSize="small" sx={{ color: '#e6f7ff' }} />
                  </IconButton>
                ))}
              </Stack>

              <Paper sx={{ p: 2, background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))', border: '1px solid rgba(255,255,255,0.06)', textAlign: 'left' }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Business sx={{ color: "#34d399" }} />
                    <Typography variant="body2" sx={{ color: "#34d399" }}>ISO Certified</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Security sx={{ color: "#f59e0b" }} />
                    <Typography variant="body2" sx={{ color: "#f59e0b" }}>100% Safety Compliant</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SupportAgent sx={{ color: "#60a5fa" }} />
                    <Typography variant="body2" sx={{ color: "#60a5fa" }}>24/7 Support</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 3 }}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="rgba(255,255,255,0.6)">
                © {new Date().getFullYear()} Namami India Support Services. All Rights Reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={3} justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center">
                <Button startIcon={<Code />} onClick={() => setOpen(true)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
                  Developer Aediax
                </Button>

                <MuiLink component={Link} to="/privacy" sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy Policy</MuiLink>
                <MuiLink component={Link} to="/terms" sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Terms</MuiLink>
                <MuiLink component={Link} to="/sitemap" sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Sitemap</MuiLink>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* --- Developer Dialog --- */}
      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { backgroundColor: '#071428', color: '#e6f7ff' } }}>
        <DialogTitle sx={{ color: '#c7f9ff' }}>
          <Code sx={{ verticalAlign: 'middle', mr: 1 }} /> Website Developed By
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="h6">{developerInfo.company}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Phone sx={{ color: '#7dd3fc' }} />
              <MuiLink href={`tel:${developerInfo.phone}`}>{developerInfo.phone}</MuiLink>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Email sx={{ color: '#7dd3fc' }} />
              <MuiLink href={`mailto:${developerInfo.email}`}>{developerInfo.email}</MuiLink>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ color: '#7dd3fc' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* --- Scroll to top button --- */}
      <AnimatePresence>
        {showTop && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{ position: 'fixed', right: 22, bottom: 22, zIndex: 1200 }}
          >
            <IconButton
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ background: '#0ea5e9', color: '#fff', width: 52, height: 52, '&:hover': { transform: 'scale(1.05)' } }}
            >
              <ArrowUpward />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
